import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { CheckCircle, ArrowRight, Percent, CreditCard, Search } from 'lucide-react';

const AboutSection = () => {
  const { t, language } = useLanguage();

  const features = [
    { icon: Percent, ...t.about.features[0] },
    { icon: CreditCard, ...t.about.features[1] },
    { icon: Search, ...t.about.features[2] }
  ];

  const benefits = language === 'en' 
    ? ['Premium quality materials', 'Expert craftsmanship', 'On-time project delivery', 'Competitive pricing']
    : ['优质材料', '专业工艺', '准时交付', '价格合理'];

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="about" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Side */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1635424824849-1b09bdcc55b1?w=800"
                alt="Roofing Professional"
                className="w-full h-[500px] object-cover"
              />
            </div>
            
            {/* Experience Badge */}
            <div className="absolute -bottom-6 -right-6 bg-slate-900 text-white p-6 rounded-2xl shadow-xl">
              <div className="text-4xl font-bold text-amber-400">20+</div>
              <div className="text-sm text-slate-400">
                {language === 'en' ? 'Years of' : '年'}
              </div>
              <div className="text-lg font-semibold">
                {language === 'en' ? 'Excellence' : '卓越服务'}
              </div>
            </div>

            {/* Decorative Element */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-amber-400/20 rounded-2xl -z-10" />
          </div>

          {/* Content Side */}
          <div className="space-y-8">
            <div>
              <span className="text-amber-600 font-semibold tracking-wide uppercase text-sm">
                {t.about.subtitle}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
                {t.about.title}
              </h2>
            </div>

            <p className="text-slate-600 text-lg leading-relaxed">
              {t.about.description}
            </p>
            <p className="text-slate-600 leading-relaxed">
              {t.about.description2}
            </p>

            {/* Benefits List */}
            <div className="grid grid-cols-2 gap-3">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <span className="text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={scrollToContact}
              className="bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 group"
            >
              {language === 'en' ? 'Get Started' : '立即开始'}
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-20">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-lg hover:border-amber-200 transition-all duration-300 group"
            >
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-500 transition-colors">
                <feature.icon className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-slate-600">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
